import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiPost, apiJson } from '../api';
import { colors, theme } from '../styles';

export default function LiveView({ account }) {
  const [screenshot, setScreenshot] = useState(null);
  const [logs, setLogs] = useState([]);
  const [connected, setConnected] = useState(false);
  const [manualControl, setManualControl] = useState(false);
  const [typingText, setTypingText] = useState('');
  const [clickDots, setClickDots] = useState([]);
  const [inputStatus, setInputStatus] = useState(null);
  const logsEndRef = useRef(null);
  const imgRef = useRef(null);

  // WebSocket for receiving screenshots and logs only
  useEffect(() => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const path = account ? `/ws/live/${account.id}` : '/ws/live';
    const ws = new WebSocket(`${proto}://${window.location.host}${path}`);

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'screenshot') {
        setScreenshot(`data:image/jpeg;base64,${msg.data}`);
      } else if (msg.type === 'log') {
        setLogs(prev => [...prev.slice(-200), msg]);
      }
    };

    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 30000);

    return () => { clearInterval(ping); ws.close(); };
  }, [account]);

  // Poll manual_control status
  useEffect(() => {
    if (!account) return;
    const poll = async () => {
      const data = await apiJson('/status');
      if (data) {
        const s = data.statuses.find(x => x.account_id === account.id);
        if (s) setManualControl(!!s.manual_control);
      }
    };
    poll();
    const i = setInterval(poll, 2000);
    return () => clearInterval(i);
  }, [account]);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // All inputs via HTTP POST
  const sendInput = useCallback(async (msg) => {
    if (!account) return;
    setInputStatus('sending...');
    try {
      const res = await apiPost(`/accounts/${account.id}/input`, msg);
      if (res) {
        setInputStatus(`OK: ${msg.action}`);
      } else {
        setInputStatus('Failed (auth?)');
      }
    } catch (e) {
      setInputStatus(`Error: ${e.message}`);
    }
    setTimeout(() => setInputStatus(null), 2000);
  }, [account]);

  const handleImageClick = useCallback((e) => {
    if (!manualControl || !imgRef.current) return;
    const img = imgRef.current;
    const rect = img.getBoundingClientRect();

    const imgNatW = 1280;
    const imgNatH = 800;
    const scale = Math.min(rect.width / imgNatW, rect.height / imgNatH);
    const renderedW = imgNatW * scale;
    const renderedH = imgNatH * scale;
    const offsetX = (rect.width - renderedW) / 2;
    const offsetY = (rect.height - renderedH) / 2;

    const x = (e.clientX - rect.left - offsetX) / scale;
    const y = (e.clientY - rect.top - offsetY) / scale;

    if (x < 0 || y < 0 || x > imgNatW || y > imgNatH) return;

    // Show click dot
    const dotX = e.clientX - rect.left;
    const dotY = e.clientY - rect.top;
    const dotId = Date.now();
    setClickDots(prev => [...prev.slice(-5), { id: dotId, x: dotX, y: dotY }]);
    setTimeout(() => setClickDots(prev => prev.filter(d => d.id !== dotId)), 1000);

    sendInput({ action: 'click', x: Math.round(x), y: Math.round(y) });
  }, [manualControl, sendInput]);

  const handleTypeSend = () => {
    if (!typingText) return;
    sendInput({ action: 'type', text: typingText });
    setTypingText('');
  };

  const handleKeyPress = (key) => {
    sendInput({ action: 'key', key });
  };

  const takeControl = async () => {
    await apiPost(`/accounts/${account.id}/take-control`);
    setManualControl(true);
  };

  const releaseControl = async () => {
    await apiPost(`/accounts/${account.id}/release-control`);
    setManualControl(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ color: '#fff', fontSize: 20, margin: 0 }}>
          Live View {account ? `: @${account.username}` : '(All Accounts)'}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {inputStatus && (
            <span style={{
              fontSize: 11, padding: '3px 8px', borderRadius: 4,
              background: inputStatus.startsWith('OK') ? colors.green + '22' : colors.orange + '22',
              color: inputStatus.startsWith('OK') ? colors.green : colors.orange,
            }}>{inputStatus}</span>
          )}
          {account && !manualControl && (
            <button style={{ ...theme.btnSmall, background: colors.orange, color: '#000', fontWeight: 600, padding: '6px 14px' }}
              onClick={takeControl}>Take Control</button>
          )}
          {account && manualControl && (
            <button style={{ ...theme.btnSmall, background: colors.green, color: '#000', fontWeight: 600, padding: '6px 14px' }}
              onClick={releaseControl}>Resume Automation</button>
          )}
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: connected ? colors.green : colors.red, display: 'inline-block' }} />
          <span style={{ fontSize: 12, color: colors.textDim }}>{connected ? 'Connected' : 'Disconnected'}</span>
        </div>
      </div>

      {manualControl && (
        <div style={{
          background: colors.orange + '22', border: `1px solid ${colors.orange}`,
          borderRadius: 6, padding: '10px 16px', marginBottom: 12,
        }}>
          <span style={{ color: colors.orange, fontWeight: 600, fontSize: 13 }}>Manual Control Active</span>
          <span style={{ color: colors.textMuted, fontSize: 12, marginLeft: 8 }}>
            Click on the browser image to interact. Type in the bar below and press Send to enter text.
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, height: manualControl ? 'calc(100vh - 260px)' : 'calc(100vh - 180px)' }}>
        {/* Browser view */}
        <div style={{ flex: 2, display: 'flex', flexDirection: 'column' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
            background: colors.bgCard, borderBottom: `1px solid ${colors.border}`,
            borderRadius: '8px 8px 0 0',
          }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f56' }} />
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ffbd2e' }} />
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#27c93f' }} />
            <span style={{ marginLeft: 8, fontSize: 12, color: colors.textDim }}>
              Browser View
              {manualControl && <span style={{ color: colors.green, marginLeft: 8 }}> - Click to interact</span>}
            </span>
          </div>
          <div style={{
            flex: 1, background: colors.bgDark,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: manualControl ? 0 : '0 0 8px 8px', overflow: 'hidden',
            cursor: manualControl ? 'crosshair' : 'default',
            position: 'relative',
          }}>
            {screenshot ? (
              <>
                <img
                  ref={imgRef}
                  src={screenshot}
                  alt="Browser"
                  style={{
                    width: '100%', height: '100%', objectFit: 'contain',
                    userSelect: 'none', cursor: manualControl ? 'crosshair' : 'default',
                    WebkitUserDrag: 'none',
                  }}
                  onClick={handleImageClick}
                  draggable={false}
                  onDragStart={e => e.preventDefault()}
                />
                {clickDots.map(dot => (
                  <div key={dot.id} style={{
                    position: 'absolute', left: dot.x - 10, top: dot.y - 10,
                    width: 20, height: 20, borderRadius: '50%',
                    background: colors.green + '66', border: `3px solid ${colors.green}`,
                    pointerEvents: 'none',
                  }} />
                ))}
              </>
            ) : (
              <div style={{ color: colors.textDimmer, fontSize: 14 }}>
                Waiting for browser session...
              </div>
            )}
          </div>

          {/* Input controls - visible during manual control */}
          {manualControl && (
            <div style={{
              display: 'flex', gap: 8, padding: '8px 12px',
              background: colors.bgCard, borderTop: `1px solid ${colors.border}`,
              borderRadius: '0 0 8px 8px', alignItems: 'center',
            }}>
              <input
                style={{ ...theme.input, flex: 1 }}
                value={typingText}
                onChange={e => setTypingText(e.target.value)}
                placeholder="Type text here, press Send"
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleTypeSend(); } }}
                autoFocus
              />
              <button style={{ ...theme.btnSmall, background: colors.accent, color: '#fff', fontWeight: 600, padding: '8px 16px' }}
                onClick={handleTypeSend}>Send</button>
              {['Tab', 'Enter', 'Escape', 'Backspace'].map(key => (
                <button key={key} style={{
                  ...theme.btnSmall, background: colors.bgInput, color: colors.textMuted,
                  border: `1px solid ${colors.borderLight}`, padding: '6px 10px', fontSize: 11,
                }} onClick={() => handleKeyPress(key)}>
                  {key === 'Backspace' ? 'Bksp' : key === 'Escape' ? 'Esc' : key}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Live log */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ ...theme.card, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <h3 style={{ ...theme.sectionTitle, marginBottom: 8 }}>Live Activity</h3>
            <div style={{ flex: 1, overflow: 'auto', fontFamily: 'monospace', fontSize: 11 }}>
              {logs.map((log, i) => (
                <div key={i} style={{ padding: '3px 0', borderBottom: `1px solid ${colors.border}11` }}>
                  <span style={{
                    color: log.status === 'error' ? colors.red :
                           log.status === 'warning' ? colors.orange :
                           log.status === 'info' ? colors.blue : colors.green,
                    fontSize: 7, marginRight: 4,
                  }}>&#9679;</span>
                  <span style={{ color: colors.textDimmer, marginRight: 6 }}>
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </span>
                  <span style={{ color: colors.accent, marginRight: 6 }}>{log.action}</span>
                  {log.detail && <span style={{ color: colors.textMuted }}>{log.detail}</span>}
                </div>
              ))}
              <div ref={logsEndRef} />
              {logs.length === 0 && (
                <div style={{ color: colors.textDimmer, padding: 16, textAlign: 'center' }}>No activity yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
